"use client";

import React, { useEffect } from "react";
import { APYProvider, type APYData } from "@/features/apy";

interface AppProvidersProps {
  apyRates: APYData;
  children: React.ReactNode;
}

/**
 * Handles hash fragment scrolling after hydration.
 * Native browser hash scrolling can fail with Next.js + animated content,
 * so we manually scroll after a short delay to ensure elements are rendered.
 */
function useHashScroll() {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const scrollToHash = () => {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    // Delay to allow hydration and animations to initialize
    const timer = setTimeout(scrollToHash, 300);
    return () => clearTimeout(timer);
  }, []);
}

export function AppProviders({ apyRates, children }: AppProvidersProps) {
  useHashScroll();

  return (
    <APYProvider rates={apyRates}>
      {children}
    </APYProvider>
  );
}